/**
 * dMRV Approvals — per-customer workspace with an 8-step pipeline stepper.
 * Left: customers who need action. Right: full step strip + focus panel
 * (approve / reject inline — no flat step table).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AppBadge,
  AppButton,
  AppField,
  AppInput,
  AppTextarea,
} from "../../../shared/components/ui";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { FieldWorkspace } from "../../../shared/components/FieldWorkspace";
import {
  FieldProcessStepper,
  type ProcessStepState,
} from "../../../shared/components/FieldProcessStepper";
import {
  IconCheck,
  IconChevronRight,
  IconHourglass,
  IconInfo,
  IconWarning,
} from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";
import { getUser } from "../../auth/session";

const STEP_SHORT: Record<number, string> = {
  1: "Chairman",
  2: "Survey",
  3: "Sales",
  4: "Install",
  5: "GPS",
  6: "Photos",
  7: "REA",
  8: "Final",
};

const STEP_FULL: Record<number, string> = {
  1: "Village Chairman",
  2: "Site Survey",
  3: "Sales",
  4: "Technical Install",
  5: "GPS Verification",
  6: "Photo Evidence",
  7: "REA / M&E Review",
  8: "Business Admin Final",
};

const STEP_OWNER: Record<number, string | null> = {
  1: "village_chairman",
  2: "technician",
  3: "sales_rep",
  4: "technician",
  5: null,
  6: "technician",
  7: "rea_officer",
  8: "business_admin",
};

function roleCanActOnStep(role: string | undefined, step: number): boolean {
  if (!role) return false;
  if (role === "business_admin") return true;
  if (step === 7 && (role === "rea_officer" || role === "m_and_e_officer")) return true;
  const owner = STEP_OWNER[step];
  if (!owner) return false;
  return role === owner;
}

type PendingDMRV = {
  id: number;
  customer_id: number;
  customer_name: string;
  customer_number: string;
  step_number: number;
  step_label: string;
  created_at: string;
  dmrv_status: string;
};

type DmrvStepDetail = {
  step: number;
  label: string;
  status: "pending" | "approved" | "rejected" | "skipped" | string;
  assigned_role?: string | null;
  actioned_at?: string | null;
  notes?: string | null;
  gps_distance_m?: number | null;
  pin_verified?: boolean | null;
};

type DmrvCustomerDetail = {
  customer_id: number;
  customer_name: string;
  area_code?: string | null;
  dmrv_status: string;
  current_step: number | null;
  steps: DmrvStepDetail[];
};

type CustomerQueueRow = {
  customer_id: number;
  customer_name: string;
  customer_number: string;
  dmrv_status: string;
  /** Lowest pending step waiting in the queue for this customer */
  focus_step: number;
  focus_label: string;
  pending_count: number;
  oldest_at: string;
};

function mapStepState(
  step: DmrvStepDetail,
  focusStep: number,
): ProcessStepState {
  if (step.status === "approved" || step.status === "skipped") return "done";
  if (step.status === "rejected") return "blocked";
  if (step.step === focusStep) return "current";
  if (step.status === "pending" && step.step < focusStep) return "blocked";
  return "todo";
}

function groupPending(records: PendingDMRV[]): CustomerQueueRow[] {
  const map = new Map<number, CustomerQueueRow>();
  for (const r of records) {
    const existing = map.get(r.customer_id);
    if (!existing) {
      map.set(r.customer_id, {
        customer_id: r.customer_id,
        customer_name: r.customer_name,
        customer_number: r.customer_number,
        dmrv_status: r.dmrv_status,
        focus_step: r.step_number,
        focus_label: r.step_label,
        pending_count: 1,
        oldest_at: r.created_at,
      });
      continue;
    }
    existing.pending_count += 1;
    if (r.step_number < existing.focus_step) {
      existing.focus_step = r.step_number;
      existing.focus_label = r.step_label;
    }
    if (r.created_at < existing.oldest_at) existing.oldest_at = r.created_at;
  }
  return [...map.values()].sort((a, b) =>
    a.oldest_at < b.oldest_at ? -1 : a.oldest_at > b.oldest_at ? 1 : 0,
  );
}

export function S8_dMRV_Dashboard() {
  const user = getUser();
  const [records, setRecords] = useState<PendingDMRV[]>([]);
  const [status, setStatus] = useState<"loading" | "error" | "success">("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<DmrvCustomerDetail | null>(null);
  const [detailStatus, setDetailStatus] = useState<"idle" | "loading" | "error">("idle");
  const [detailError, setDetailError] = useState("");
  const [focusStep, setFocusStep] = useState<number>(1);
  const [action, setAction] = useState<"approve" | "reject">("approve");
  const [pin, setPin] = useState("");
  const [notes, setNotes] = useState("Community verification confirmed");
  const [gpsLat, setGpsLat] = useState("");
  const [gpsLng, setGpsLng] = useState("");
  const [photoPath, setPhotoPath] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const queue = useMemo(() => groupPending(records), [records]);
  const selected = queue.find((q) => q.customer_id === selectedId) ?? queue[0] ?? null;

  const fetchPending = useCallback(async () => {
    setStatus("loading");
    try {
      const data = await api.get<{ records: PendingDMRV[] }>("/dmrv/pending");
      const list = data.records || [];
      setRecords(list);
      setStatus("success");
      const grouped = groupPending(list);
      setSelectedId((prev) => {
        if (prev && grouped.some((g) => g.customer_id === prev)) return prev;
        return grouped[0]?.customer_id ?? null;
      });
    } catch (err: unknown) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Failed to load pending dMRV");
    }
  }, []);

  useEffect(() => {
    void fetchPending();
  }, [fetchPending]);

  const loadDetail = useCallback(async (customerId: number, preferredStep?: number) => {
    setDetailStatus("loading");
    setDetailError("");
    try {
      const data = await api.get<DmrvCustomerDetail>(`/customers/${customerId}/dmrv`);
      setDetail(data);
      const pending = data.steps
        .filter((s) => s.status === "pending")
        .sort((a, b) => a.step - b.step);
      const next =
        preferredStep && data.steps.some((s) => s.step === preferredStep)
          ? preferredStep
          : pending[0]?.step ?? data.current_step ?? 1;
      setFocusStep(next);
      setDetailStatus("idle");
      setAction("approve");
      setPin("");
      setNotes(
        next === 1
          ? "Community verification confirmed"
          : `Step ${next} reviewed`,
      );
      setFormError("");
    } catch (err: unknown) {
      setDetail(null);
      setDetailStatus("error");
      setDetailError(err instanceof Error ? err.message : "Could not load dMRV pipeline");
    }
  }, []);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    void loadDetail(selected.customer_id, selected.focus_step);
  }, [selected?.customer_id, selected?.focus_step, loadDetail]);

  const focused = detail?.steps.find((s) => s.step === focusStep) ?? null;
  const canAct =
    focused?.status === "pending"
    && roleCanActOnStep(user?.role, focusStep)
    && focusStep !== 5;

  const captureBrowserGps = () => {
    if (!navigator.geolocation) {
      setFormError("Geolocation is not available on this device");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsLat(String(pos.coords.latitude));
        setGpsLng(String(pos.coords.longitude));
        setFormError("");
      },
      () => setFormError("Could not capture GPS — enter coordinates manually"),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const submitAction = async () => {
    if (!selected || !focused || !canAct) return;
    setFormError("");
    const needsPin = focused.step === 1 && action === "approve";
    if (needsPin) {
      const digits = pin.replace(/\D/g, "");
      if (digits.length < 4) {
        setFormError("Village Chairman PIN must be at least 4 digits");
        return;
      }
    }
    if (action === "reject" && !notes.trim()) {
      setFormError("A rejection note is required");
      return;
    }
    if (action === "approve" && (focused.step === 2 || focused.step === 4)) {
      if (!gpsLat.trim() || !gpsLng.trim()) {
        setFormError("Latitude and longitude are required for this step");
        return;
      }
    }
    if (action === "approve" && focused.step === 6 && !photoPath.trim()) {
      setFormError("Photo evidence path/URL is required for step 6");
      return;
    }
    setBusy(true);
    try {
      const body: Record<string, string | number> = { notes: notes.trim() };
      if (needsPin) body.pin = pin.replace(/\D/g, "");
      if (action === "approve" && (focused.step === 2 || focused.step === 4)) {
        body.latitude = parseFloat(gpsLat);
        body.longitude = parseFloat(gpsLng);
      }
      if (action === "approve" && focused.step === 6) {
        body.photo_path = photoPath.trim();
      }
      await api.post(
        `/customers/${selected.customer_id}/dmrv/${focused.step}/${action}`,
        body,
      );
      setGpsLat("");
      setGpsLng("");
      setPhotoPath("");
      await fetchPending();
      await loadDetail(selected.customer_id);
    } catch (err: unknown) {
      setFormError(err instanceof Error ? err.message : "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const listStatus =
    status === "success" && queue.length === 0 ? "empty" : status;

  const stepperSteps = useMemo(() => {
    const steps = detail?.steps?.length
      ? detail.steps
      : [1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({
          step: n,
          label: STEP_FULL[n],
          status: "pending" as const,
        }));
    return steps.map((s) => ({
      label: STEP_SHORT[s.step] ?? `S${s.step}`,
      state: mapStepState(s, focusStep),
      onSelect: () => setFocusStep(s.step),
    }));
  }, [detail, focusStep]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <PageHeader
        title="dMRV Approvals"
        subtitle={
          user?.role === "village_chairman"
            ? "Open a household, confirm Step 1 with your PIN."
            : "Pick a household — walk the 8-step pipeline, act on the open step."
        }
      />

      <PageState
        status={listStatus}
        error={errorMsg}
        onRetry={fetchPending}
        emptyTitle="No households waiting"
        emptyHint="When a step needs your role, the customer appears here with their pipeline."
      >
        <FieldWorkspace
          left={
            <>
              <div style={{ marginBottom: "var(--pg-s3)" }}>
                <h3 style={{ margin: 0 }}>Households</h3>
                <p
                  style={{
                    margin: "6px 0 0",
                    color: "var(--pg-text-muted)",
                    fontSize: "var(--pg-fs-meta)",
                  }}
                >
                  {queue.length} with open dMRV work
                </p>
              </div>
              {queue.map((row) => {
                const active = selected?.customer_id === row.customer_id;
                return (
                  <button
                    key={row.customer_id}
                    type="button"
                    className="pg-lead-row"
                    onClick={() => setSelectedId(row.customer_id)}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      marginBottom: 8,
                      outline: active ? "2px solid var(--pg-green)" : "none",
                    }}
                  >
                    <span>
                      <div className="lead-name">{row.customer_name}</div>
                      <div className="lead-meta">
                        {row.customer_number} · Step {row.focus_step} {STEP_SHORT[row.focus_step]}
                      </div>
                    </span>
                    <span className="lead-tail">
                      <AppBadge tone="warning">
                        <IconHourglass size={12} /> {row.pending_count} open
                      </AppBadge>
                      <IconChevronRight size={14} />
                    </span>
                  </button>
                );
              })}
            </>
          }
          right={
            !selected ? (
              <div className="pg-state" style={{ padding: "var(--pg-s5)" }}>
                <span className="state-icon info">
                  <IconInfo size={40} />
                </span>
                <h2>Select a household</h2>
                <p>Each customer has an 8-step dMRV pipeline.</p>
              </div>
            ) : detailStatus === "loading" && !detail ? (
              <div className="pg-state" style={{ padding: "var(--pg-s5)" }}>
                <span className="state-icon info">
                  <IconHourglass size={40} />
                </span>
                <h2>Loading pipeline…</h2>
              </div>
            ) : detailStatus === "error" ? (
              <div className="pg-state" style={{ padding: "var(--pg-s5)" }}>
                <span className="state-icon warning">
                  <IconWarning size={40} />
                </span>
                <h2>Could not load pipeline</h2>
                <p>{detailError}</p>
                <AppButton
                  type="button"
                  variant="secondary"
                  onClick={() => void loadDetail(selected.customer_id, selected.focus_step)}
                >
                  Retry
                </AppButton>
              </div>
            ) : (
              <>
                <div className="pg-lifecycle-head" style={{ marginBottom: 8 }}>
                  <div>
                    <h3 style={{ margin: 0 }}>{selected.customer_name}</h3>
                    <p
                      style={{
                        margin: "4px 0 0",
                        color: "var(--pg-text-muted)",
                        fontSize: "var(--pg-fs-meta)",
                      }}
                    >
                      {selected.customer_number}
                      {detail?.area_code ? ` · ${detail.area_code}` : ""}
                      {detail?.dmrv_status ? ` · ${detail.dmrv_status}` : ""}
                    </p>
                  </div>
                  <AppButton
                    type="button"
                    variant="secondary"
                    onClick={() =>
                      void loadDetail(selected.customer_id, selected.focus_step)
                    }
                  >
                    Refresh
                  </AppButton>
                </div>

                <FieldProcessStepper
                  variant="panel"
                  ariaLabel="dMRV pipeline"
                  steps={stepperSteps}
                />

                <div className="pg-summary" style={{ marginTop: 12 }}>
                  <div className="row">
                    <span className="k">Focus</span>
                    <span className="v">
                      Step {focusStep} — {STEP_FULL[focusStep] ?? focused?.label}
                    </span>
                  </div>
                  <div className="row">
                    <span className="k">Status</span>
                    <span className="v">
                      {focused?.status === "approved" ? (
                        <AppBadge tone="success">
                          <IconCheck size={12} /> Approved
                        </AppBadge>
                      ) : focused?.status === "rejected" ? (
                        <AppBadge tone="error">Rejected</AppBadge>
                      ) : focused?.status === "pending" ? (
                        <AppBadge tone="warning">Needs action</AppBadge>
                      ) : (
                        <AppBadge tone="neutral">{focused?.status ?? "—"}</AppBadge>
                      )}
                    </span>
                  </div>
                  {focused?.assigned_role ? (
                    <div className="row">
                      <span className="k">Owner role</span>
                      <span className="v">{focused.assigned_role.replace(/_/g, " ")}</span>
                    </div>
                  ) : null}
                  {focused?.actioned_at ? (
                    <div className="row">
                      <span className="k">Actioned</span>
                      <span className="v">
                        {new Date(focused.actioned_at).toLocaleString()}
                      </span>
                    </div>
                  ) : null}
                  {focused?.gps_distance_m != null ? (
                    <div className="row">
                      <span className="k">GPS distance</span>
                      <span className="v">{focused.gps_distance_m.toFixed(1)} m</span>
                    </div>
                  ) : null}
                  {focused?.notes ? (
                    <div className="row">
                      <span className="k">Notes</span>
                      <span className="v">{focused.notes}</span>
                    </div>
                  ) : null}
                </div>

                {focused?.status === "pending" ? (
                  <div
                    style={{
                      marginTop: 16,
                      padding: 16,
                      border: "1px solid var(--pg-border)",
                      borderRadius: 8,
                      display: "grid",
                      gap: 12,
                    }}
                  >
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <AppButton
                        type="button"
                        variant={action === "approve" ? "primary" : "secondary"}
                        onClick={() => {
                          setAction("approve");
                          setFormError("");
                        }}
                      >
                        Approve
                      </AppButton>
                      <AppButton
                        type="button"
                        variant={action === "reject" ? "primary" : "secondary"}
                        onClick={() => {
                          setAction("reject");
                          setFormError("");
                        }}
                      >
                        Reject
                      </AppButton>
                    </div>

                    {focusStep === 1 && action === "approve" ? (
                      <AppField
                        label="Village Chairman PIN (4+ digits)"
                        error={
                          formError.includes("PIN") ? formError : undefined
                        }
                      >
                        <AppInput
                          value={pin}
                          onChange={(e) => setPin(e.target.value)}
                          placeholder="1234"
                          autoComplete="one-time-code"
                          disabled={busy}
                        />
                      </AppField>
                    ) : null}

                    {(focusStep === 2 || focusStep === 4) && action === "approve" ? (
                      <>
                        <AppField label="GPS latitude">
                          <AppInput
                            value={gpsLat}
                            onChange={(e) => setGpsLat(e.target.value)}
                            placeholder="-6.7712"
                            disabled={busy}
                          />
                        </AppField>
                        <AppField label="GPS longitude">
                          <AppInput
                            value={gpsLng}
                            onChange={(e) => setGpsLng(e.target.value)}
                            placeholder="39.2399"
                            disabled={busy}
                          />
                        </AppField>
                        <AppButton type="button" variant="secondary" onClick={captureBrowserGps} disabled={busy}>
                          Capture GPS from device
                        </AppButton>
                      </>
                    ) : null}

                    {focusStep === 6 && action === "approve" ? (
                      <AppField label="Photo evidence URL / path">
                        <AppInput
                          value={photoPath}
                          onChange={(e) => setPhotoPath(e.target.value)}
                          placeholder="/uploads/install-photo.jpg"
                          disabled={busy}
                        />
                      </AppField>
                    ) : null}

                    {focused?.status === "pending" && !canAct ? (
                      <div className="pg-inline-error" role="alert">
                        <IconWarning size={14} /> This step is owned by{" "}
                        {(focused.assigned_role || STEP_OWNER[focusStep] || "another role").replace(/_/g, " ")}.
                        Your role cannot approve it here.
                      </div>
                    ) : null}

                    <AppField label="Notes">
                      <AppTextarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        disabled={busy}
                      />
                    </AppField>

                    {formError && !formError.includes("PIN") ? (
                      <div className="pg-inline-error" role="alert">
                        {formError}
                      </div>
                    ) : null}

                    <AppButton
                      type="button"
                      disabled={busy || !canAct}
                      onClick={() => void submitAction()}
                    >
                      {busy
                        ? "Saving…"
                        : action === "approve"
                          ? `Confirm approve step ${focusStep}`
                          : `Confirm reject step ${focusStep}`}
                    </AppButton>
                  </div>
                ) : (
                  <p
                    style={{
                      marginTop: 16,
                      color: "var(--pg-text-muted)",
                      fontSize: "var(--pg-fs-meta)",
                    }}
                  >
                    {focused?.status === "approved"
                      ? "This step is done — tap another step on the strip, or pick the next open household."
                      : focused?.status === "rejected"
                        ? "This step was rejected — earlier steps may need rework."
                        : "Waiting on earlier steps before this one can be actioned."}
                  </p>
                )}
              </>
            )
          }
        />
      </PageState>
    </div>
  );
}
